import React, {
  useContext,
  createContext,
  useReducer,
  useEffect,
  PropsWithChildren,
} from 'react';

import { UserViewModel } from '@api/index';
import { removeStorageItems } from '@utils/utils';
import { getRequestHeaders } from '@api/caller';

import { Action, ACTIONS, AuthState, LoginInfo } from './types';

export type { LoginInfo };

const initialValues = {
  userInfo: {
    id: 0,
    firstName: '',
    lastName: '',
    isTurkishCitizen: true,
    userName: '',
    totalRewardAmount: 0,
    isSeturEmployee: false,
  },
  tokenExpirationDate: null,
};

type AuthDispatch = (action: Action) => void;

const AuthStateContext = createContext<AuthState | undefined>(undefined);
export const AuthDispatchContext = createContext<AuthDispatch | undefined>(
  undefined
);

const hotelDetailReducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case ACTIONS.SET_USER_INFO:
      return { ...state, userInfo: action.payload };

    case ACTIONS.SET_TOKEN_EXPIRATION_DATE:
      return { ...state, tokenExpirationDate: action.payload };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const AuthProvider = ({
  children,
  loginInfo,
}: PropsWithChildren<{ loginInfo?: LoginInfo }>) => {
  const [state, dispatch] = useReducer(hotelDetailReducer, initialValues);
  const userInfo =
    typeof window !== 'undefined' && localStorage.getItem('userInfo');

  const tokenExpiration =
    typeof window !== 'undefined' && localStorage.getItem('tokenExpiration');

  useEffect(() => {
    if (state.userInfo.id) {
      const userId = String(state.userInfo.id);
      localStorage.setItem('userId', userId);
    }
  }, [state.userInfo.id]);

  useEffect(() => {
    if (userInfo) {
      dispatch({ type: ACTIONS.SET_USER_INFO, payload: JSON.parse(userInfo) });
    }
  }, [userInfo]);

  useEffect(() => {
    if (tokenExpiration) {
      dispatch({
        type: ACTIONS.SET_TOKEN_EXPIRATION_DATE,
        payload: new Date(tokenExpiration),
      });
    }
  }, [tokenExpiration]);
  return (
    <AuthStateContext.Provider value={{ ...state, loginInfo }}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

export const useAuthState = (): AuthState => {
  const context = useContext(AuthStateContext);
  if (context === undefined) {
    throw new Error('useAuthState must be used within a AuthProvider');
  }
  return context;
};

export const useSetUserInfo = (): ((payload: UserViewModel) => void) => {
  const dispatch = useContext(AuthDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetUserInfo must be used within a AuthProvider');
  }
  return (payload: UserViewModel) =>
    dispatch({ type: ACTIONS.SET_USER_INFO, payload: payload });
};

export const useSetTokenExpirationDate = (): ((payload: Date) => void) => {
  const dispatch = useContext(AuthDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetUserInfo must be used within a AuthProvider');
  }
  return (payload: Date) =>
    dispatch({ type: ACTIONS.SET_TOKEN_EXPIRATION_DATE, payload: payload });
};

export const useClearDispatch = () => {
  const dispatch = useContext(AuthDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a AuthProvider');
  }
  return async () => {
    dispatch({ type: ACTIONS.CLEAR });
    const headers = getRequestHeaders();
    await fetch('/api/logout', { headers });
    removeStorageItems([
      'userInfo',
      'token',
      'tokenExpiration',
      'loyalties',
      'userId',
    ]);
  };
};

export default AuthProvider;

export const DISCONNECTION_REASONS = {
  SESSION_EXPIRATION: 'SESSION_EXPIRATION',
};
export const DISCONNECTION_REASON_STORAGE_KEY = 'DISCONNECTION_REASON';
